/**
 * T-A17：ImageCompressWorker 池 + 读信号量（同一 WORKER_POOL 上限）。
 */
import { UPLOAD_CONFIG } from './uploadConfig';
import { createImageCompressWorker } from './imageCompressWorkerFactory';

let nextJobId = 1;

/** @type {ImageCompressWorkerPool|null} */
let sharedPool = null;

export function isImageCompressWorkerSupported() {
  return typeof Worker !== 'undefined'
    && typeof OffscreenCanvas !== 'undefined'
    && typeof createImageBitmap === 'function'
    && typeof crypto !== 'undefined'
    && typeof crypto.subtle?.digest === 'function';
}

export function isWorkerCompressEnabled(config = UPLOAD_CONFIG) {
  return config?.workerCompress === true && isImageCompressWorkerSupported();
}

export function getUploadWorkerPoolSize(config = UPLOAD_CONFIG) {
  return Math.min(8, Math.max(1, Number(config?.workerPool) || 4));
}

/**
 * §3.10：Worker Blob → 带 name 的 File
 * @param {Blob} blob
 * @param {File} originalFile
 * @param {string} [mime]
 */
export function wrapCompressedUploadFile(blob, originalFile, mime) {
  if (!blob) {
    throw new Error('invalid_blob');
  }
  const type = String(mime || blob.type || 'image/jpeg');
  const name = String(originalFile?.name || '').trim() || 'image.jpg';
  if (!name) {
    throw new Error('empty_file_name');
  }
  return new File([blob], name, {
    type,
    lastModified: Number(originalFile?.lastModified) || Date.now(),
  });
}

function createWorkerInstance() {
  return createImageCompressWorker();
}

class ImageCompressWorkerPool {
  /**
   * @param {number} size
   * @param {{ createWorker?: () => Worker }} [deps]
   */
  constructor(size, deps = {}) {
    this.size = Math.max(1, size | 0);
    this.createWorker = deps.createWorker || createWorkerInstance;
    /** @type {Worker[]} */
    this.workers = [];
    /** @type {Worker[]} */
    this.idle = [];
    /** @type {Array<{ job: object, resolve: Function, reject: Function }>} */
    this.waitQueue = [];
    this.readActive = 0;
    /** @type {Array<() => void>} */
    this.readWaiters = [];
    this.disposed = false;
    this._ensureWorkers();
  }

  _ensureWorkers() {
    while (this.workers.length < this.size) {
      const worker = this.createWorker();
      worker.onmessage = (ev) => this._onWorkerMessage(worker, ev);
      worker.onerror = (err) => this._onWorkerError(worker, err);
      this.workers.push(worker);
      this.idle.push(worker);
    }
  }

  async withReadSlot(fn) {
    await this._acquireRead();
    try {
      return await fn();
    } finally {
      this._releaseRead();
    }
  }

  _acquireRead() {
    return new Promise((resolve) => {
      const tryAcquire = () => {
        if (this.readActive < this.size) {
          this.readActive += 1;
          resolve();
          return;
        }
        this.readWaiters.push(tryAcquire);
      };
      tryAcquire();
    });
  }

  _releaseRead() {
    this.readActive = Math.max(0, this.readActive - 1);
    const next = this.readWaiters.shift();
    if (next) next();
  }

  /**
   * 读缓冲 + Worker 处理在同一槽内：禁止齐射 arrayBuffer。
   * @param {File} file
   * @param {object} options
   */
  async compressFile(file, options = {}) {
    if (this.disposed) {
      throw new Error('worker_pool_disposed');
    }
    return this.withReadSlot(async () => {
      const buffer = await file.arrayBuffer();
      const result = await this._runJob({
        buffer,
        mimeHint: file.type || options.mimeHint || 'image/jpeg',
        jpegQuality: options.jpegQuality,
        compressMaxEdgePx: options.compressMaxEdgePx,
        rejectMaxEdgePx: options.rejectMaxEdgePx,
      });
      return result;
    });
  }

  _runJob(payload) {
    return new Promise((resolve, reject) => {
      const job = {
        id: nextJobId++,
        ...payload,
      };
      const entry = { job, resolve, reject };
      const worker = this.idle.shift();
      if (worker) {
        this._dispatch(worker, entry);
      } else {
        this.waitQueue.push(entry);
      }
    });
  }

  _dispatch(worker, entry) {
    worker.__ipeJob = entry;
    const { buffer, ...meta } = entry.job;
    try {
      worker.postMessage({ ...meta, buffer }, [buffer]);
    } catch (err) {
      worker.__ipeJob = null;
      this.idle.push(worker);
      entry.reject(err);
      this._pumpQueue();
    }
  }

  _onWorkerMessage(worker, ev) {
    const entry = worker.__ipeJob;
    worker.__ipeJob = null;
    this.idle.push(worker);
    if (entry) {
      entry.resolve(ev?.data || { ok: false, error: 'empty_response' });
    }
    this._pumpQueue();
  }

  _onWorkerError(worker, err) {
    const entry = worker.__ipeJob;
    worker.__ipeJob = null;
    this.idle.push(worker);
    if (entry) {
      entry.reject(err || new Error('worker_error'));
    }
    this._pumpQueue();
  }

  _pumpQueue() {
    while (this.waitQueue.length > 0 && this.idle.length > 0) {
      const entry = this.waitQueue.shift();
      const worker = this.idle.shift();
      this._dispatch(worker, entry);
    }
  }

  getStats() {
    return {
      size: this.size,
      readActive: this.readActive,
      idleWorkers: this.idle.length,
      queued: this.waitQueue.length,
    };
  }

  dispose() {
    this.disposed = true;
    for (const w of this.workers) {
      try {
        w.terminate();
      } catch {
        /* ignore */
      }
    }
    this.workers = [];
    this.idle = [];
    this.waitQueue = [];
    this.readWaiters = [];
    this.readActive = 0;
  }
}

export function createImageCompressWorkerPool(size = getUploadWorkerPoolSize(), deps = {}) {
  return new ImageCompressWorkerPool(size, deps);
}

export function getSharedImageCompressWorkerPool() {
  const size = getUploadWorkerPoolSize();
  if (!sharedPool || sharedPool.disposed || sharedPool.size !== size) {
    if (sharedPool) {
      try {
        sharedPool.dispose();
      } catch {
        /* ignore */
      }
    }
    sharedPool = createImageCompressWorkerPool(size);
  }
  return sharedPool;
}

/** @visibleForTesting */
export function resetSharedImageCompressWorkerPoolForTests() {
  if (sharedPool) {
    try {
      sharedPool.dispose();
    } catch {
      /* ignore */
    }
  }
  sharedPool = null;
}

/**
 * @param {File} file
 * @param {{
 *   jpegQuality?: number,
 *   compressMaxEdgePx?: number,
 *   rejectMaxEdgePx?: number,
 *   pool?: ImageCompressWorkerPool,
 * }} [options]
 * @returns {Promise<{ ok: true, file: File, contentHash: string, width: number, height: number, mime: string }
 *   | { ok: false, message: string, error?: string, contentHash?: string }>}
 */
export async function compressUploadFileWithWorker(file, options = {}) {
  if (!file) {
    return { ok: false, message: '无效文件', error: 'invalid_file' };
  }
  const pool = options.pool || getSharedImageCompressWorkerPool();
  let result;
  try {
    result = await pool.compressFile(file, {
      jpegQuality: options.jpegQuality ?? UPLOAD_CONFIG.jpegQuality,
      compressMaxEdgePx: options.compressMaxEdgePx ?? UPLOAD_CONFIG.maxEdgePx,
      rejectMaxEdgePx: options.rejectMaxEdgePx || 0,
    });
  } catch (err) {
    return {
      ok: false,
      message: '图片压缩失败，请换一张重试',
      error: 'worker_exception',
      detail: String(err?.message || err),
    };
  }

  if (!result?.ok || !result.blob) {
    if (result?.error === 'dimension_exceeded') {
      return {
        ok: false,
        message: options.dimensionExceededMessage || '分辨率不可以大于4096x4096无法上传',
        error: 'dimension_exceeded',
        contentHash: result.contentHash || '',
      };
    }
    return {
      ok: false,
      message: '图片压缩失败，请换一张重试',
      error: result?.error || 'compress_failed',
      contentHash: result?.contentHash || '',
    };
  }

  const compressedFile = wrapCompressedUploadFile(result.blob, file, result.mime);
  if (result.contentHash) {
    try {
      Object.defineProperty(compressedFile, '_contentHash', {
        value: result.contentHash,
        enumerable: false,
        configurable: true,
      });
    } catch {
      compressedFile._contentHash = result.contentHash;
    }
  }
  return {
    ok: true,
    file: compressedFile,
    contentHash: result.contentHash || '',
    width: result.width,
    height: result.height,
    mime: result.mime,
  };
}

export { ImageCompressWorkerPool };
